<script setup lang="ts">
import { computed } from 'vue'
import type { ProductOwnerActivityHour } from '../stores/product_owner_runs'
import { buildHourlyHeatmapDays } from '../composables/productOwnerActivityHourlyHeatmap'
import {
  heatmapCellColorClass,
  heatmapCellTitle,
  HEATMAP_LEVELS,
  HEATMAP_NO_DATA_LEVEL,
} from '../composables/productOwnerActivityHeatmap'

// Per-repo hourly drill-down for the Activity tab (#1210): one column per
// day in the selected window, one small cell per hour, colored by that
// hour's idle percentage for the one selected repo. Same cell shape and
// color scale as ProductOwnerActivityHeatmap.vue's daily/droplet-wide
// heatmap — reused via buildHourlyHeatmapDays/heatmapCellColorClass rather
// than a parallel color scheme, so the two heatmaps read as one visual
// language.
const props = defineProps<{
  hours: ProductOwnerActivityHour[]
  windowHours: number
}>()

const days = computed(() => buildHourlyHeatmapDays(props.hours, props.windowHours))

const legend = [
  { label: HEATMAP_NO_DATA_LEVEL.label, class: HEATMAP_NO_DATA_LEVEL.colorClass },
  ...HEATMAP_LEVELS.toReversed().map((level) => ({ label: level.label, class: level.colorClass })),
]
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="product-owner-activity-repo-heatmap">
    <div class="flex gap-[3px] overflow-x-auto pb-1">
      <div v-for="(day, di) in days" :key="di" class="flex flex-col gap-[3px]">
        <div
          v-for="(cell, hi) in day.cells"
          :key="hi"
          class="w-3 h-3 rounded-sm"
          :class="heatmapCellColorClass(cell)"
          :title="heatmapCellTitle(cell)"
          :data-testid="cell ? `hourly-heatmap-cell-${di}-${hi}` : undefined"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/50">
      <span v-for="item in legend" :key="item.label" class="flex items-center gap-1.5">
        <span class="w-3 h-3 rounded-sm inline-block" :class="item.class" />
        {{ item.label }}
      </span>
    </div>
  </div>
</template>
